import React, { useRef, ChangeEvent } from 'react';
import { v4 as uuid_v4 } from 'uuid';
import { useApolloClient, gql } from '@apollo/client';
import { openDoc } from 'utils/open-doc';
import './styles.scss';
import { PngIcon } from 'components/menuItem/PngItem';

const GET_UPLOAD_URL = gql`query GetUploadFileName($uploadFileName: String) {getNewFileUrl(fileName: $uploadFileName)}`;

interface IBitFile extends File{
  url:string;
  newName:string
}

interface SingleFileUploaderProps {
  onFileUploaded: (bitFile:IBitFile)=>void,
  onFileDeleted: ()=>void,
  uploadedFileUrl:string,
  disabled:boolean,
  width?:string,
  accept:string,
  bucketUrl:string
  maxFileSize:number,
  children?: any,
}

export const SingleFileUploader:React.FC<SingleFileUploaderProps> = ({
  onFileUploaded,
  onFileDeleted = () => {},
  uploadedFileUrl = '',
  disabled = false,
  bucketUrl = '',
  accept,
  maxFileSize = 1000000,
  children
}) => {

  const gqlClient = useApolloClient();

  const humanFileName =
    uploadedFileUrl?.substring?.(
      uploadedFileUrl.lastIndexOf('|') === -1 ? uploadedFileUrl.lastIndexOf('/') + 1 : uploadedFileUrl.indexOf('|') + 1
    ) || '';

  const uploadFile = async(file:File, progressCb:(loaded:number, total:number)=>void):Promise<IBitFile> => {
    const uploadFileName = `${uuid_v4()}|${file.name}`;
    const qlResponse = await gqlClient.query({ query: GET_UPLOAD_URL, variables: { uploadFileName } });

    if (qlResponse.errors || !qlResponse.data.getNewFileUrl) return Promise.reject();

    const uploadUrl = qlResponse.data.getNewFileUrl;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.upload.onprogress = (e) => progressCb(e.loaded, e.total);
      xhr.onload = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            const newFile:IBitFile = {
              ...file,
              url: bucketUrl + uploadFileName,
              newName: uploadFileName
            };
            resolve(newFile);
          } else {
            reject();
          }
        }
      };
      xhr.onerror = () => reject();
      xhr.send(file);
    });
  };

  const inputFile = useRef<HTMLInputElement>(null);

  const handleFileUpload = (e:ChangeEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const files = e.target.files;
    const filesArray = Array.from(files ?? []);

    if (filesArray?.length && filesArray[0].size > maxFileSize) {
      alert(`File is too large! Maximum size is ${maxFileSize / (1024 * 1024)}MB`);
      input.value = ''; // Clear the selection
      return;
    }

    if (filesArray.length) uploadFile(filesArray[0], () => {}).then((file) => onFileUploaded(file));
  };
  return (
    <div style={{ display: 'flex', width: '100%' }}>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}
        className='single-file-uploader-folder-button'
        onClick={() => !disabled && inputFile.current?.click()}
      >
        <PngIcon iconType='folder' size={23}/>
        {uploadedFileUrl ? (
          <div className='single-file-uploader-remove-button'>
            <a className='single-file-uploader-link'
              href={uploadedFileUrl}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => openDoc(uploadedFileUrl)}
            >
              <div>
                {humanFileName}
              </div>
            </a>
            <PngIcon iconType='cross'
              onClick={(e:any) => {
                e.stopPropagation();
                if (inputFile.current) inputFile.current.value = '';
                uploadedFileUrl && onFileDeleted();
              }
              }/>
          </div>
        ) : (
          <div className='single-file-uploader-nofile-text'>
            <span>файл не завантажений</span>
          </div>
        )}
      </div>
      <input
        // style={{ display: 'none' }}
        style={{ visibility: 'hidden', width: 0, height: '32px' }}
        accept={accept}
        ref={inputFile}
        onChange={handleFileUpload}
        type='file'
      />
      {children}
    </div>
  );
};
